import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import ItemCard from '../components/ItemCard';
import ModalConfirm from '../components/ModalConfirm';
import Modal from '../components/ui/Modal';
import Field, { FormRow } from '../components/ui/Field';
import Estado from '../components/ui/Estado';
import EmptyState from '../components/ui/EmptyState';
import PageHeader from '../components/ui/PageHeader';
import Icon from '../components/ui/Icon';
import { toast } from '../components/Toast';
import { fmtBRL, fmtData, hoje } from '../utils/format';

const RECORRENCIAS = [
  { id: 'NENHUMA', label: 'Não recorrente' },
  { id: 'SEMANAL', label: 'Semanal' },
  { id: 'MENSAL', label: 'Mensal' },
  { id: 'ANUAL', label: 'Anual' },
];

const RECORRENCIA_LABEL = {
  SEMANAL: 'Semanal',
  MENSAL: 'Mensal',
  ANUAL: 'Anual',
};

const FORM_VAZIO = {
  dataVencimento: hoje(),
  beneficiario: '',
  valor: '',
  descricao: '',
  recorrencia: 'NENHUMA',
  compraIds: [],
};

function totalCompra(c) {
  return (c.quantidade || 0) * (c.custoUnitario || 0);
}

function rotuloCompra(c) {
  const nome = c.insumo?.nome || 'Insumo';
  const forn = c.fornecedor ? ` · ${c.fornecedor}` : '';
  return `${fmtData(c.dataCompra)} — ${nome}${forn} (${fmtBRL(totalCompra(c))})`;
}

function payloadDoForm(form) {
  const recorrente = form.recorrencia && form.recorrencia !== 'NENHUMA';
  return {
    dataVencimento: form.dataVencimento,
    beneficiario: form.beneficiario.trim(),
    // Recorrente: valor é estimativa opcional; envia null quando em branco.
    valor: form.valor === '' || form.valor == null ? (recorrente ? null : parseFloat(form.valor)) : parseFloat(form.valor),
    descricao: form.descricao?.trim() || null,
    recorrencia: form.recorrencia || 'NENHUMA',
    compraIds: recorrente ? [] : (form.compraIds || []),
  };
}

export default function ContasAPagar() {
  const [lista, setLista] = useState([]);
  const [compras, setCompras] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const [form, setForm] = useState({ ...FORM_VAZIO });
  /* O cadastro virou modal. Antes o formulário ficava aberto no topo da página
     e a lista nascia abaixo da dobra no celular — só que esta tela é aberta
     muitas vezes para consultar vencimentos e poucas para cadastrar. */
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [erroModal, setErroModal] = useState('');
  const [excluindo, setExcluindo] = useState(null);
  const [pagando, setPagando] = useState(null); // { conta, valor, pagoEm }
  const [salvando, setSalvando] = useState(false);
  const [filtro, setFiltro] = useState('PENDENTES'); // PENDENTES | TODAS | PAGAS
  const [expandidaId, setExpandidaId] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErroCarga('');
    try {
      const [contas, comps] = await Promise.all([
        api.getContasAPagar(),
        api.getCompras(),
      ]);
      setLista(contas || []);
      setCompras(comps || []);
    } catch (err) {
      setErroCarga(err.message || 'Não foi possível carregar as contas.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const comprasPorId = useMemo(() => {
    const m = new Map();
    compras.forEach((c) => m.set(c.id, c));
    return m;
  }, [compras]);

  const aplicarVinculoCompras = (ids, base) => {
    const selecionadas = ids.map((id) => comprasPorId.get(id)).filter(Boolean);
    const soma = selecionadas.reduce((acc, c) => acc + totalCompra(c), 0);
    const forn = selecionadas.find((c) => c.fornecedor)?.fornecedor || '';
    return {
      ...base,
      compraIds: ids,
      valor: soma > 0 ? String(Number(soma.toFixed(2))) : base.valor,
      beneficiario: base.beneficiario?.trim()
        ? base.beneficiario
        : (forn || base.beneficiario),
    };
  };

  const toggleCompra = (compraId, modoEdit) => {
    if (modoEdit) {
      setEditando((e) => {
        const ids = e.compraIds.includes(compraId)
          ? e.compraIds.filter((id) => id !== compraId)
          : [...e.compraIds, compraId];
        return aplicarVinculoCompras(ids, e);
      });
    } else {
      setForm((f) => {
        const ids = f.compraIds.includes(compraId)
          ? f.compraIds.filter((id) => id !== compraId)
          : [...f.compraIds, compraId];
        return aplicarVinculoCompras(ids, f);
      });
    }
  };

  const salvar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      await api.criarContaAPagar(payloadDoForm(form));
      setForm({ ...FORM_VAZIO, dataVencimento: hoje() });
      setCriando(false);
      toast('Conta a pagar cadastrada. Aviso 2 dias antes do vencimento.', 'success');
      carregar();
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setSalvando(false);
    }
  };

  const abrirEdicao = (c) => {
    setErroModal('');
    setEditando({
      id: c.id,
      dataVencimento: (c.dataVencimento || '').slice(0, 10),
      beneficiario: c.beneficiario || '',
      valor: c.valor == null ? '' : String(c.valor),
      descricao: c.descricao || '',
      recorrencia: c.recorrencia || 'NENHUMA',
      compraIds: (c.compras || []).map((x) => x.id),
    });
  };

  const salvarEdicao = async (e) => {
    e?.preventDefault();
    setErroModal('');
    try {
      await api.editarContaAPagar(editando.id, payloadDoForm(editando));
      setEditando(null);
      toast('Conta atualizada.', 'success');
      carregar();
    } catch (err) {
      setErroModal(err.message);
    }
  };

  // Abre o modal de pagamento. Para recorrentes o valor pode mudar a cada
  // ciclo (ex.: conta de luz), então perguntamos valor + data.
  const abrirPagamento = (c) => {
    const estimado = c.cicloAtualValorPago ?? c.valor;
    setPagando({
      conta: c,
      valor: estimado == null ? '' : String(estimado),
      pagoEm: hoje(),
    });
  };

  const confirmarPagamento = async (e) => {
    e?.preventDefault();
    const { conta, valor, pagoEm } = pagando;
    const body = {
      valor: valor === '' || valor == null ? null : parseFloat(valor),
      pagoEm: pagoEm || hoje(),
    };
    try {
      await api.marcarContaPaga(conta.id, body);
      setPagando(null);
      toast('Pagamento registrado.', 'success');
      carregar();
    } catch (err) {
      toast(err.message, 'error');
    }
  };

  const reabrir = async (c) => {
    try {
      await api.reabrirContaAPagar(c.id);
      toast(c.recorrente ? 'Pagamento do ciclo desfeito.' : 'Conta reaberta como pendente.', 'success');
      carregar();
    } catch (err) {
      toast(err.message, 'error');
    }
  };

  const confirmarExclusao = async () => {
    try {
      await api.excluirContaAPagar(excluindo.id);
      setExcluindo(null);
      toast('Conta removida.', 'success');
      carregar();
    } catch (err) {
      setExcluindo(null);
      toast(err.message, 'error');
    }
  };

  // Uma conta comum está "quitada" pelo status; a recorrente, pelo ciclo atual.
  const estaQuitada = (c) => (c.recorrente ? c.cicloAtualPago : c.status === 'PAGO');

  const listaFiltrada = lista.filter((c) => {
    if (filtro === 'PENDENTES') return !estaQuitada(c);
    if (filtro === 'PAGAS') return estaQuitada(c);
    return true;
  });

  // Recorrentes sempre aparecem, sob o próprio título, mesmo que o ciclo já
  // esteja pago (aí mostram "Paga até ..." em verde).
  const recorrentes = listaFiltrada.filter((c) => c.recorrente);
  const comuns = listaFiltrada.filter((c) => !c.recorrente);

  const compraIdsVinculadosExceto = useCallback((excludeContaId) => {
    const ids = new Set();
    for (const conta of lista) {
      if (excludeContaId != null && conta.id === excludeContaId) continue;
      for (const c of conta.compras || []) {
        if (c?.id != null) ids.add(c.id);
      }
    }
    return ids;
  }, [lista]);

  const seletorCompras = (ids, modoEdit, excludeContaId = null) => {
    const bloqueadas = compraIdsVinculadosExceto(excludeContaId);
    return (
      <div className="seletor-compras">
        {compras.length === 0 ? (
          <p className="seletor-compras__vazio">
            Nenhuma compra cadastrada. Você pode vincular depois em{' '}
            <Link to="/insumos">Insumos</Link>.
          </p>
        ) : compras.map((c) => {
          const bloqueada = bloqueadas.has(c.id);
          return (
            <label
              key={c.id}
              className={`seletor-compras__opcao ${bloqueada ? 'seletor-compras__opcao--bloqueada' : ''}`}
            >
              <input
                type="checkbox"
                checked={ids.includes(c.id)}
                disabled={bloqueada}
                onChange={() => toggleCompra(c.id, modoEdit)}
              />
              <span>
                {rotuloCompra(c)}
                {bloqueada && (
                  <span className="seletor-compras__aviso">Já vinculada a uma conta a pagar</span>
                )}
              </span>
            </label>
          );
        })}
      </div>
    );
  };

  const camposConta = (dados, aplicar, modoEdit, excludeContaId = null) => {
    const recorrente = dados.recorrencia && dados.recorrencia !== 'NENHUMA';
    return (
      <>
        <FormRow>
          <Field label={recorrente ? 'Primeiro vencimento' : 'Dia do pagamento'} obrigatorio>
            <input
              type="date"
              value={dados.dataVencimento}
              onChange={(e) => aplicar((x) => ({ ...x, dataVencimento: e.target.value }))}
              required
            />
          </Field>
          <Field label="Para quem pagar" obrigatorio>
            <input
              value={dados.beneficiario}
              onChange={(e) => aplicar((x) => ({ ...x, beneficiario: e.target.value }))}
              required
              placeholder="Ex: Cooperativa, João, Banco"
            />
          </Field>
          <Field label="Repetir">
            <select
              value={dados.recorrencia || 'NENHUMA'}
              onChange={(e) => aplicar((x) => ({ ...x, recorrencia: e.target.value }))}
            >
              {RECORRENCIAS.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
          </Field>
        </FormRow>
        <FormRow>
          <Field
            label={recorrente ? 'Valor estimado (R$)' : 'Valor (R$)'}
            obrigatorio={!recorrente}
          >
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={dados.valor}
              onChange={(e) => aplicar((x) => ({ ...x, valor: e.target.value }))}
              required={!recorrente}
              placeholder="0,00"
            />
          </Field>
        </FormRow>
        {recorrente && (
          <p className="dica">
            Conta {RECORRENCIA_LABEL[dados.recorrencia]?.toLowerCase()}: o valor pode mudar a cada
            ciclo. Você confirma o valor real na hora de pagar (ex.: conta de luz).
          </p>
        )}
        <Field label="Observação (opcional)">
          <input
            value={dados.descricao}
            onChange={(e) => aplicar((x) => ({ ...x, descricao: e.target.value }))}
            placeholder="Anotação livre sobre esta conta"
            maxLength={500}
          />
        </Field>
        {!recorrente && (
          <Field label={modoEdit ? 'Compras vinculadas' : 'Vincular compras de insumos (opcional)'}>
            {seletorCompras(dados.compraIds, modoEdit, excludeContaId)}
          </Field>
        )}
      </>
    );
  };

  const renderConta = (c) => {
    const recorrente = c.recorrente;
    const quitada = estaQuitada(c);
    const aberta = expandidaId === c.id;
    // Vencimento em foco: para recorrente é o ciclo vigente; para comum é o
    // próprio vencimento.
    const vencFoco = recorrente ? (c.cicloAtualVencimento || c.dataVencimento) : c.dataVencimento;
    const dias = (() => {
      if (!vencFoco || quitada) return null;
      const hojeD = new Date(`${hoje()}T12:00:00`);
      const ven = new Date(`${String(vencFoco).slice(0, 10)}T12:00:00`);
      return Math.round((ven - hojeD) / 86400000);
    })();
    return (
      <div key={c.id} className="item-expansivel">
        <div
          role="button"
          tabIndex={0}
          aria-expanded={aberta}
          onClick={() => setExpandidaId(aberta ? null : c.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setExpandidaId(aberta ? null : c.id);
            }
          }}
          style={{ cursor: 'pointer' }}
        >
          <ItemCard
            onEdit={(quitada && !recorrente) ? undefined : () => abrirEdicao(c)}
            onDelete={() => setExcluindo(c)}
            actions={(
              <button
                type="button"
                className="item-card-btn"
                onClick={() => (quitada ? reabrir(c) : abrirPagamento(c))}
              >
                {quitada ? 'Reabrir' : 'Marcar paga'}
              </button>
            )}
          >
            <div className="item-conteudo">
              <div className="item-titulo">
                {c.beneficiario}
                {recorrente && (
                  <span className="selo selo--info" style={{ marginLeft: 8 }}>
                    {RECORRENCIA_LABEL[c.recorrencia] || 'Recorrente'}
                  </span>
                )}
              </div>
              <div className="item-meta">
                {recorrente ? 'Vence neste ciclo em ' : 'Vence '}{fmtData(vencFoco)}
                {(!recorrente && c.compras?.length > 0) ? ` · ${c.compras.length} compra(s)` : ''}
              </div>
              {!quitada && dias != null && (
                <div className={`item-nota ${dias < 0 ? 'item-nota--atraso' : dias <= 2 ? 'item-nota--proximo' : ''}`}>
                  {dias < 0 ? `Atrasada ${Math.abs(dias)} dia(s)` : dias === 0 ? 'Vence hoje' : `Em ${dias} dia(s)`}
                </div>
              )}
              {quitada && recorrente && c.cicloAtualVencimento && (
                <div className="item-nota item-nota--ok">
                  Paga até {fmtData(c.cicloAtualVencimento)}
                  {c.cicloAtualValorPago != null ? ` · ${fmtBRL(c.cicloAtualValorPago)}` : ''}
                </div>
              )}
              {quitada && !recorrente && c.pagoEm && (
                <div className="item-nota item-nota--ok">Paga em {fmtData(c.pagoEm)}</div>
              )}
            </div>
            <div className="item-valor">
              {recorrente && c.valor == null ? '—' : fmtBRL(recorrente ? (c.cicloAtualValorPago ?? c.valor) : c.valor)}
            </div>
            <span className="item-expandir" aria-hidden="true">
              <Icon nome={aberta ? 'chevron-cima' : 'chevron-baixo'} tamanho={16} />
            </span>
          </ItemCard>
        </div>

        {aberta && (
          <div className="painel-detalhe">
            <div className="painel-detalhe__bloco">
              <div className="painel-detalhe__rotulo">Observação</div>
              <div>{c.descricao?.trim() ? c.descricao : '—'}</div>
            </div>
            {recorrente ? (
              <div className="painel-detalhe__bloco">
                <div className="painel-detalhe__rotulo">Histórico de pagamentos</div>
                {(c.pagamentos?.length > 0) ? (
                  <ul>
                    {c.pagamentos.map((p) => (
                      <li key={p.id}>
                        {fmtData(p.competencia)} — {fmtBRL(p.valor)}
                        {p.pagoEm ? ` (pago em ${fmtData(p.pagoEm)})` : ''}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div style={{ color: 'var(--text-muted)' }}>Nenhum ciclo pago ainda</div>
                )}
              </div>
            ) : (
              <div className="painel-detalhe__bloco">
                <div className="painel-detalhe__rotulo">Insumos vinculados</div>
                {(c.compras?.length > 0) ? (
                  <ul>
                    {c.compras.map((compra) => (
                      <li key={compra.id}>{rotuloCompra(compra)}</li>
                    ))}
                  </ul>
                ) : (
                  <div style={{ color: 'var(--text-muted)' }}>Nenhum insumo vinculado</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <PageHeader
        titulo="Contas a pagar"
        icone="conta-pagar"
        descricao="Vencimentos e para quem pagar. Dois dias antes do vencimento o aviso aparece em Notificações."
        acao={{ label: 'Nova conta', icone: 'adicionar', onClick: () => setCriando(true) }}
      />

      <div className="card">
        <div className="secao-titulo">
          <div className="chips">
            {[
              { id: 'PENDENTES', label: 'Pendentes' },
              { id: 'PAGAS', label: 'Pagas' },
              { id: 'TODAS', label: 'Todas' },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                className={`chip ${filtro === f.id ? 'chip--ativo' : ''}`}
                onClick={() => setFiltro(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          {lista.length > 0 && <span className="contador">{listaFiltrada.length}</span>}
        </div>

        <Estado carregando={carregando} erro={erroCarga} aoTentarNovamente={carregar} itens={3}>
          {listaFiltrada.length === 0 ? (
            <EmptyState
              compacto
              icone="conta-pagar"
              titulo={filtro === 'PAGAS' ? 'Nenhuma conta paga' : 'Nenhuma conta a pagar'}
              descricao="Cadastre um vencimento para receber o aviso dois dias antes."
              acao={filtro === 'PAGAS' ? undefined : { label: 'Nova conta', onClick: () => setCriando(true) }}
            />
          ) : (
            <>
              {comuns.length > 0 && (
                <div className="lista-cards">
                  {comuns.map(renderConta)}
                </div>
              )}

              {recorrentes.length > 0 && (
                <>
                  <div className="secao-titulo secao-titulo--divisor" style={{ marginTop: comuns.length > 0 ? 'var(--sp-5)' : 0 }}>
                    <h2>Contas recorrentes</h2>
                    <span className="contador">{recorrentes.length}</span>
                  </div>
                  <div className="lista-cards">
                    {recorrentes.map(renderConta)}
                  </div>
                </>
              )}
            </>
          )}
        </Estado>
      </div>

      {criando && (
        <Modal titulo="Nova conta a pagar" onClose={() => setCriando(false)} largura={560}>
          <form onSubmit={salvar}>
            {camposConta(form, setForm, false)}
            <div className="actions modal-rodape">
              <button type="button" className="btn-secondary" onClick={() => setCriando(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={salvando}>
                {salvando ? 'Salvando...' : 'Cadastrar conta'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editando && (
        <Modal titulo="Editar conta a pagar" onClose={() => setEditando(null)} largura={560}>
          {erroModal && <div className="aviso aviso--erro">{erroModal}</div>}
          <form onSubmit={salvarEdicao}>
            {camposConta(editando, setEditando, true, editando.id)}
            <div className="actions modal-rodape">
              <button type="button" className="btn-secondary" onClick={() => setEditando(null)}>Cancelar</button>
              <button type="submit" className="btn-primary">Salvar</button>
            </div>
          </form>
        </Modal>
      )}

      {pagando && (
        <Modal
          titulo={pagando.conta.recorrente ? 'Registrar pagamento do ciclo' : 'Registrar pagamento'}
          onClose={() => setPagando(null)}
          largura={460}
        >
          <form onSubmit={confirmarPagamento}>
            {pagando.conta.recorrente && pagando.conta.cicloAtualVencimento && (
              <p className="dica">
                Ciclo com vencimento em {fmtData(pagando.conta.cicloAtualVencimento)}.
                Confirme o valor pago neste período.
              </p>
            )}
            <FormRow>
              <Field label="Valor pago (R$)" obrigatorio={pagando.conta.recorrente && pagando.conta.valor == null}>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={pagando.valor}
                  onChange={(e) => setPagando((p) => ({ ...p, valor: e.target.value }))}
                  placeholder="0,00"
                  autoFocus
                />
              </Field>
              <Field label="Pago em" obrigatorio>
                <input
                  type="date"
                  value={pagando.pagoEm}
                  onChange={(e) => setPagando((p) => ({ ...p, pagoEm: e.target.value }))}
                  required
                />
              </Field>
            </FormRow>
            <div className="actions modal-rodape">
              <button type="button" className="btn-secondary" onClick={() => setPagando(null)}>Cancelar</button>
              <button type="submit" className="btn-primary">Confirmar pagamento</button>
            </div>
          </form>
        </Modal>
      )}

      {excluindo && (
        <ModalConfirm
          titulo="Excluir conta"
          mensagem={<>Excluir conta de <strong>{excluindo.beneficiario}</strong>{excluindo.valor != null ? ` (${fmtBRL(excluindo.valor)})` : ''}?{excluindo.recorrente ? ' Todo o histórico de pagamentos será removido.' : ''}</>}
          onConfirm={confirmarExclusao}
          onCancel={() => setExcluindo(null)}
        />
      )}
    </div>
  );
}
